/**
 * Pure helpers turning CSV-export outcomes into LOW-CARDINALITY analytics
 * properties. Never send raw error text, row counts, or any identifying data
 * (see the analytics-events rules) — these buckets exist so release telemetry
 * can answer "why do big exports fail" (timeout vs connection vs one
 * unreadable row; huge-cell-capable columns present or not) without it.
 */

export type ExportFailureClass = 'timeout' | 'connection' | 'row-unreadable' | 'other';

/** Classify a partial-export error message into an analytics bucket. */
export function classifyExportFailure(error: string): ExportFailureClass {
  // Checked first — the single-row recovery failure message embeds the
  // underlying timeout text, and the specific class is the interesting one.
  if (/could not be read/i.test(error)) return 'row-unreadable';
  if (/timeout|timed out/i.test(error)) return 'timeout';
  if (/connect/i.test(error)) return 'connection';
  return 'other';
}

export type RowCountBucket = 'lt-100k' | '100k-1m' | 'gte-1m' | 'unknown';

export type SkippedRowsBucket = '1' | '2-5' | '6-20' | 'gt-20';

/**
 * Bucket how many rows an export skipped (exact counts are high-cardinality).
 * Callers only send this when rows WERE skipped — call with count >= 1.
 * '6-20' is the per-run cap range; 'gt-20' can only happen when a resume
 * chain merges skips across runs.
 */
export function bucketSkippedRows(count: number): SkippedRowsBucket {
  if (count <= 1) return '1';
  if (count <= 5) return '2-5';
  if (count <= 20) return '6-20';
  return 'gt-20';
}

/** Bucket a result-set size (exact row counts are high-cardinality). */
export function bucketRowCount(total: number): RowCountBucket {
  if (total <= 0) return 'unknown';
  if (total < 100_000) return 'lt-100k';
  if (total < 1_000_000) return '100k-1m';
  return 'gte-1m';
}

/**
 * True when any column can hold very large values — the giant-cell suspects
 * behind deterministic export stalls (a 100-row subset of multi-MB cells can
 * be too big for the server to return in time).
 *
 * Two detection paths, because neither alone covers every engine:
 * - `isLong`: authoritative when present. SQL Tools Service (MSSQL) reports
 *   `dataTypeName` WITHOUT the size qualifier — `nvarchar(max)` arrives as
 *   just `"nvarchar"` — so the regex below would false-negative on STS-sourced
 *   columns. STS's wire format separately flags LOB/unbounded columns via
 *   `isLong`, which we trust when set.
 * - `dataType` regex: still needed for engines/paths that don't set `isLong`
 *   but do include the size qualifier or a LOB type name in the type string.
 */
export function hasWideValueColumns(columns: { dataType?: string; isLong?: boolean }[]): boolean {
  return columns.some((col) => {
    if (col.isLong === true) return true;
    const t = col.dataType;
    if (!t) return false;
    const lower = t.toLowerCase();
    if (/\bmax\b/.test(lower)) return true; // nvarchar(max), varbinary(max)
    return /\b(text|ntext|image|xml|json|jsonb|blob|clob|nclob|bytea|longtext|mediumtext|longblob|mediumblob|long|bfile)\b/.test(
      lower,
    );
  });
}
